import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useChatsStore } from "@/stores/chats.store";
import type { ReadChatDto } from "@package/types";
import { Copy, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface ShareChatDialogProps {
  chatId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function ShareChatDialog({
  chatId,
  onOpenChange
}: ShareChatDialogProps) {
  const { t } = useTranslation();
  const chat = useChatsStore((s) => s.chats.find((c) => c.id === chatId));
  const updateChat = useChatsStore((s) => s.updateChat);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const shareUrl = chat?.shareToken
    ? `${window.location.origin}/share/${chat.shareToken}`
    : null;

  const setShared = async (shared: boolean) => {
    if (!chat) return;

    try {
      setIsUpdating(true);
      const updated = await api.patch<ReadChatDto>(`/chats/${chat.id}/share`, {
        shared
      });
      updateChat(updated);
    } catch (error) {
      console.error("Failed to update chat sharing:", error);
      toast.error(t("chats.shareError"));
    } finally {
      setIsUpdating(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("chats.shareCopied"));
    } catch {
      // clipboard API is unavailable on insecure origins; leave the link selected instead
      inputRef.current?.select();
      toast.error(t("chats.shareCopyError"));
    }
  };

  return (
    <Dialog open={!!chat} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("chats.shareTitle")}</DialogTitle>
          <DialogDescription>
            {shareUrl
              ? t("chats.shareActiveDescription")
              : t("chats.shareDescription")}
          </DialogDescription>
        </DialogHeader>

        {shareUrl && (
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={shareUrl}
              readOnly
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={copyLink}
              aria-label={t("chats.shareCopy")}
            >
              <Copy className="size-4" />
            </Button>
          </div>
        )}

        <DialogFooter>
          {shareUrl ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShared(false)}
              disabled={isUpdating}
            >
              {isUpdating ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("chats.shareStop")}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => setShared(true)}
              disabled={isUpdating}
            >
              {isUpdating ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("chats.shareCreate")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
